/**
 * Calculate relative luminance of a color
 * Based on WCAG 2.0 formula
 * @param hex - Hex color string (e.g., "#3b0b86")
 * @returns Relative luminance value between 0 and 1
 */
function getRelativeLuminance(hex: string): number {
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;
  
  // Apply gamma correction
  const rLinear = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const gLinear = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const bLinear = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
  
  // Calculate relative luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * Get contrast ratio between two colors
 * @param luminance1 - Relative luminance of first color
 * @param luminance2 - Relative luminance of second color
 * @returns Contrast ratio
 */
function getContrastRatio(luminance1: number, luminance2: number): number {
  const lighter = Math.max(luminance1, luminance2);
  const darker = Math.min(luminance1, luminance2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Determine whether to use black or white text on a given background color
 * @param backgroundColor - Hex color string of the background
 * @returns "white" or "black" for optimal contrast
 */
export function getTextColorForBackground(backgroundColor: string): 'white' | 'black' {
  const bgLuminance = getRelativeLuminance(backgroundColor);
  const whiteLuminance = 1; // White has luminance of 1
  const blackLuminance = 0; // Black has luminance of 0
  
  const contrastWithWhite = getContrastRatio(bgLuminance, whiteLuminance);
  const contrastWithBlack = getContrastRatio(bgLuminance, blackLuminance);
  
  // Return the color with better contrast
  return contrastWithWhite > contrastWithBlack ? 'white' : 'black';
}

/**
 * Get the hex color code for text based on background
 * @param backgroundColor - Hex color string of the background
 * @returns Hex color string for text ("#ffffff" or "#000000")
 */
export function getTextColorHex(backgroundColor: string): string {
  return getTextColorForBackground(backgroundColor) === 'white' ? '#ffffff' : '#000000';
}

// Brand colors - precomputed for performance
export const brandTextColors = {
  vividPurple: getTextColorForBackground('#3b0b86'), // white
  vividOrange: getTextColorForBackground('#ff6417'), // black or white
  vividCyan: getTextColorForBackground('#01ffff'), // black
  vividYellowGreen: getTextColorForBackground('#e1f433'), // black
} as const;
